<script context="module" lang="ts">
  import Flash from "@shared/services/flashv2.js";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import * as _ from "lodash";
  import type { SavedProductDict, SavedProductType } from "@shared/types/SavedProducts";
  import type { CartProduct } from "@shared/types/ProductLine";
  import type { CartOrderType } from "@shared/types/Cart";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import {
    CartEventEntity,
    CartOrderEntity,
    DistributorEntity,
    ProductEntity,
  } from "@shared/services/event_tracking/entities";
  import {
    shouldUseRetailerFulfillmentMethodForDistributor,
    getDistributorFulfillmentMethod,
  } from "@retailer/services/fulfillment_methods_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  const { trackEvent } = EventTracking;

  export const saveOrderForLater = function (Cart, order: CartOrderType) {
    const limitByWarehouse = false;
    const warehouseId = null;
    saveOrderGroupForLater(Cart, order, limitByWarehouse, warehouseId);
  };

  export const saveOrderForLaterByWarehouse = function (Cart, order: CartOrderType, warehouseId) {
    const limitByWarehouse = true;
    saveOrderGroupForLater(Cart, order, limitByWarehouse, warehouseId);
  };

  const saveOrderGroupForLater = function (
    Cart,
    order: CartOrderType,
    limitByWarehouse,
    warehouseId,
  ) {
    if (!order) {
      return;
    }
    if (window["$rootScope"].showLoading == true) {
      return;
    }
    window["$rootScope"].showLoading = true;
    trackEvent("Save Order For Later Selected", [
      new CartEventEntity({
        location: "Retailer - Cart",
        distributor: order.distributor.name,
      }),
      new CartOrderEntity({
        orderId: order.id,
        distributorId: order.distributor.id,
      }),
    ]);
    HttpPromise({
      method: "POST",
      url: "/api/retailer/saved_products/save_entire_order",
      data: {
        order: {
          id: order.id,
          limit_by_warehouse: limitByWarehouse,
          warehouse_id: warehouseId,
        },
      },
    })
      .then(function success(res) {
        Flash.now({
          message: "Your order for " + order.distributor.name + " has been saved for later.",
          type: "success",
        });
        delete window["$rootScope"].savedProducts;
        Cart.load();
      })
      .catch(function (res) {
        window["$rootScope"].showLoading = false;
        if (_.get(res, "status") == 404) {
          Flash.now({
            message: "We had a problem saving that order - please refresh and try again",
            type: "alert",
          });
        } else if (_.includes([401, 403], _.get(res, "status"))) {
          Flash.now({
            message: "We had a problem saving that order - please try again",
            type: "alert",
          });
        } else {
          throw res;
        }
      });
  };

  export const saveForLater = function (Cart, product: CartProduct, cb) {
    if (window["$rootScope"].showLoading == true) {
      return Promise.resolve();
    }
    window["$rootScope"].showLoading = true;
    product.saved = true;
    trackEvent("Save For Later Selected", [
      new CartEventEntity({
        location: "Retailer - Cart",
        product_line: product.product_line.name,
      }),
      new CartOrderEntity({
        distributorId: product.distributor_id,
        orderId: product.order_id,
      }),
      new ProductEntity({
        productId: product.id,
      }),
    ]);

    return Cart.removeProduct(product, function (res) {
      HttpPromise({
        method: "POST",
        url: "/api/retailer/saved_products",
        data: {
          saved_product: {
            product_id: product.product_id,
            product_line_id: product.product_line.id,
            inventory_id: product.inventory_id,
            distributor_id: product.distributor_id,
            unit_quantity: product.unit_quantity,
            case_quantity: product.case_quantity,
          },
        },
      }).then(
        function success(res) {
          var np: SavedProductType = res;
          var sp: SavedProductDict = window["$rootScope"].savedProducts;
          if (sp[np.distributor.name]) {
            sp[np.distributor.name].push(np);
          } else {
            sp[np.distributor.name] = [np];
          }
          window["$rootScope"].savedProducts = sp;
          cb(sp);
          window["$rootScope"].savedProductsCount += 1;
          window["$rootScope"].showLoading = false;
        },
        function error(res) {
          // if the product is already in the saved products,
          // do not do anything extra here.
          window["$rootScope"].showLoading = false;
        },
      );
    });
  };

  const canUseWineCellar = distributor =>
    stagedRolloutCheck("marketplace__fulfillment_methods") && distributor?.uses_wine_cellar;

  export const moveToCart = async function (Cart, sp: SavedProductType, cb) {
    if (window["$rootScope"].showLoading == true) {
      return;
    }
    window["$rootScope"].showLoading = true;
    trackEvent("Move To Cart Selected", [
      new CartEventEntity({
        location: "Retailer - Cart Save For Later",
        product_line: sp.product_line.name,
      }),
      new DistributorEntity({
        id: sp.distributor_id,
      }),
      new ProductEntity({
        productId: sp.id,
      }),
    ]);

    let warehouse_id = null;
    if (
      shouldUseRetailerFulfillmentMethodForDistributor(
        sp.distributor_id,
        sp.distributor.uses_warehouse_pickup,
        canUseWineCellar(sp.distributor),
      )
    ) {
      const distributorFulfillmentMethod = await getDistributorFulfillmentMethod(sp.distributor_id);
      warehouse_id = distributorFulfillmentMethod?.warehouse_id;
    }

    Cart.add(
      {
        inventory_id: sp.inventory_id,
        unit_quantity: sp.unit_quantity,
        case_quantity: sp.case_quantity,
        warehouse_id: warehouse_id || null,
      },
      function (res) {
        doDelete(sp, true, function (res) {
          window["$rootScope"].showLoading = false;
          if (cb) cb(res);
        });
      },
    );
  };

  const getDistributor = function (distributorName: string) {
    return window["$rootScope"].savedProducts[distributorName][0].distributor;
  };

  export const moveOrderToCart = async function (Cart, distributorName: string) {
    if (window["$rootScope"].showLoading == true) {
      return;
    }
    window["$rootScope"].showLoading = true;
    trackEvent(
      "Move Order Back To Cart Selected",
      new CartEventEntity({
        location: "Retailer - Cart Save For Later",
        distributor: distributorName,
      }),
    );

    var distributor = getDistributor(distributorName);
    let warehouse_id = null;
    if (
      shouldUseRetailerFulfillmentMethodForDistributor(
        distributor.id,
        distributor.uses_warehouse_pickup,
        canUseWineCellar(distributor),
      )
    ) {
      const distributorFulfillmentMethod = await getDistributorFulfillmentMethod(distributor.id);
      warehouse_id = distributorFulfillmentMethod?.warehouse_id;
    }

    HttpPromise({
      method: "POST",
      url: "/api/retailer/saved_products/restore_entire_order",
      data: {
        distributor: {
          id: distributor.id,
          warehouse_id: warehouse_id || null,
        },
      },
    }).then(
      function success(res) {
        Flash.now({
          message: `<p style="display:inline" class="fizz-text-strong">${distributor.name}</p>
                    <p style="display:inline"> items were moved to cart.</p>`,
          type: "success",
          rawHtml: true,
        });

        delete window["$rootScope"].savedProducts;
        Cart.load();
      },
      function error(res) {
        window["$rootScope"].showLoading = false;
      },
    );
  };

  const removeFromSavedProducts = function (sp) {
    var dp: SavedProductType[] = window["$rootScope"].savedProducts[sp.distributor.name];
    var toRemove: SavedProductType = _.find(dp, function (x) {
      return x.id == sp.id;
    });
    _.remove(dp, function (x) {
      return x.id == toRemove.id;
    });
    if (dp.length == 0) {
      delete window["$rootScope"].savedProducts[sp.distributor.name];
    }
    window["$rootScope"].savedProductsCount -= 1;
  };

  export const doDelete = function (sp: SavedProductType, skipUndo: boolean, cb) {
    window["$rootScope"].showLoading = true;
    trackEvent("Remove From Cart Selected", [
      new CartEventEntity({
        location: "Retailer - Cart Save For Later",
        product_line: sp.product_line.name,
      }),
      new ProductEntity({ productId: sp.id }),
      new DistributorEntity({ id: sp.distributor_id }),
    ]);

    HttpPromise({
      method: "DELETE",
      url: "/api/retailer/saved_products/" + sp.id,
    }).then(
      function success(res) {
        if (skipUndo) {
          removeFromSavedProducts(sp);
          cb(window["$rootScope"].savedProducts);
        } else {
          sp.deleted = true;
          window["$rootScope"].savedProductsCount -= 1;
          cb(window["$rootScope"].savedProducts);
        }
        window["$rootScope"].showLoading = false;
      },
      function error(res) {
        window["$rootScope"].showLoading = false;
      },
    );
  };

  export const undoDelete = function (sp: SavedProductType, cb) {
    if (window["$rootScope"].showLoading == true) {
      return;
    }
    window["$rootScope"].showLoading = true;
    trackEvent(
      "Undo Remove From Cart Selected",
      new CartEventEntity({
        location: "Retailer - Cart Save For Later",
        product_line: sp.product_line.name,
      }),
    );

    HttpPromise({
      method: "POST",
      url: "/api/retailer/saved_products",
      data: {
        saved_product: {
          product_id: sp.product_id,
          product_line_id: sp.product_line.id,
          inventory_id: sp.inventory_id,
          distributor_id: sp.distributor_id,
          unit_quantity: sp.unit_quantity,
          case_quantity: sp.case_quantity,
        },
      },
    }).then(
      function success(res) {
        sp.deleted = false;
        sp.id = res.id;
        window["$rootScope"].savedProductsCount += 1;
        window["$rootScope"].showLoading = false;
        cb(window["$rootScope"].savedProducts);
      },
      function error(res) {
        window["$rootScope"].showLoading = false;
      },
    );
  };
</script>
